import React, { useContext, useEffect, useState } from "react";

import { PopupContentContext } from "../../../context/PopupContentContext";
import { OrganizationInfoResponse } from "../../../api/organization/get";
import { CurrentMember, OrganizationMember, PendingMember, isCurrentMember, isPendingMember } from "./types";
import { copyTextToClipboard, sendButtonAnalytics, sendOrganizationAnalytics } from "../../../utils";
import { UserInfoResponse } from "../../../api/user/info";
import MenuContent, { Menubar, MenubarItem, MenubarTrigger } from "../../../components/elements/menu";
import revokeInvitation from "../../../api/organization/member/invite/delete";
import resendInvite from "../../../api/organization/member/invite/put";
import { getOrganizationSettings } from "../../../api/organization/settings/get";
import { DeleteMemberModal } from "../../../components/modals/settings-modals/DeleteMemberModal";
import { CreateOrgModal } from "../../../components/modals/settings-modals/CreateOrganizationModal";
import { InviteMemberModal } from "../../../components/modals/settings-modals/InviteMemberModal";
import {
    canDeleteMember,
    canUpdateMemberRole,
    canCopyInviteLink,
    canRevokeInvite,
    canResendInvite,
    canInviteMember,
    capitalizeFirstLetter
} from "./utils";
import { assertNever } from "../utils";
import { UpdateMemberRoleModal } from "../../../components/modals/settings-modals/UpdateMemberRoleModal";
import OverlaySpinner from "../../../components/elements/OverlaySpinner";

type MemberManagementState =
    | { status: "create-organization" }
    | { status: "invite-member" }
    | { status: "delete-member"; member: CurrentMember }
    | { status: "update-member-role"; member: CurrentMember }
    | {
          status: "idle";
      };

type Props = {
    orgMembers: OrganizationMember[];
    user: UserInfoResponse;
    onMemberDeleted: (email: string) => void;
    onOrgCreated: (org: OrganizationInfoResponse) => void;
    onMembersInvited: (pendingMembers: PendingMember[]) => void;
    org: OrganizationInfoResponse;
    onMemberUpdated: (member: CurrentMember) => void;
};

export function MemberManagement({
    orgMembers,
    user,
    onMemberDeleted,
    onOrgCreated,
    onMembersInvited,
    org,
    onMemberUpdated
}: Props) {
    const [state, setState] = useState<MemberManagementState>({ status: "idle" });
    const { showToast } = useContext(PopupContentContext);
    const [maxNumberOfMembers, setMaxNumberOfMembers] = useState(3);
    const [isProcessing, setIsProcessing] = useState(false);

    async function getOrgSettings() {
        try {
            setIsProcessing(true);
            const response = await getOrganizationSettings();
            setMaxNumberOfMembers(response.maxMemberAllowed);
        } catch (e) {
        } finally {
            setIsProcessing(false);
        }
    }

    useEffect(() => {
        getOrgSettings();
    }, []);

    async function handleCopyInviteLink(pendingMember: PendingMember) {
        if (!org.organizationFound) {
            return;
        }
        try {
            await copyTextToClipboard(pendingMember.invitationLink);
            sendOrganizationAnalytics("invite_link_copied", {
                orgId: org.organizationId,
                author: { role: org.role, id: user.userId, email: user.email },
                invitedMember: { email: pendingMember.email }
            });
            showToast({
                status: "success",
                icon: `/static/assets/dashboard/link.svg`,
                children: <>Invite link copied successfully!</>
            });
        } catch (e) {
            showToast({
                status: "error",
                icon: `/static/assets/dashboard/red-issue.png`,
                children: <>Invite link copy failed</>
            });
        }
    }

    async function handleResendInviteLink(pendingMember: PendingMember) {
        if (!org.organizationFound) {
            return;
        }
        try {
            await resendInvite(pendingMember.email);
            sendOrganizationAnalytics("invite_resend", {
                orgId: org.organizationId,
                author: { role: org.role, id: user.userId, email: user.email },
                invitedMember: { email: pendingMember.email }
            });
            showToast({
                status: "success",
                icon: `/static/assets/dashboard/resend-icon.svg`,
                children: <>Invite resent successfully!</>
            });
        } catch (e) {
            showToast({
                status: "error",
                icon: `/static/assets/dashboard/red-issue.png`,
                children: <>Failed to resend invite</>
            });
        }
    }
    async function handleRevokeInviteLink(pendingMember: PendingMember) {
        if (!org.organizationFound) {
            return;
        }
        setIsProcessing(true);
        try {
            await revokeInvitation(pendingMember.email);
            sendOrganizationAnalytics("invite_revoke", {
                orgId: org.organizationId,
                author: { role: org.role, id: user.userId, email: user.email },
                invitedMember: { email: pendingMember.email }
            });
            showToast({
                status: "error",
                icon: `/static/assets/dashboard/red-delete.svg`,
                children: <>Invite deleted successfully</>
            });
            onMemberDeleted(pendingMember.email);
        } catch (e) {
            showToast({
                status: "error",
                icon: `/static/assets/dashboard/red-issue.png`,
                children: <>Failed to delete invite</>
            });
        } finally {
            setIsProcessing(false);
        }
    }

    function renderHeader() {
        const shouldUpdateOrgName = !org.organizationFound || !org.organizationName;
        const cannotInviteMemberToOrg =
            org.organizationFound &&
            !canInviteMember({
                role: org.role,
                email: user.email
            });

        return cannotInviteMemberToOrg ? (
            <div className="separated-flex padded-x" id="manage-users-header">
                <span className="heading">Organization Members</span>
            </div>
        ) : (
            <div className="separated-flex padded-x" id="manage-users-header">
                <span className="heading lg-only">Manage and invite organization members</span>
                <span className="heading md-only">Manage members</span>

                <button
                    className="button-with-image yellow-button large-padded-button centered"
                    onClick={() => {
                        setState({ status: shouldUpdateOrgName ? "create-organization" : "invite-member" });
                        sendButtonAnalytics("button_invite_member_to_org");
                    }}
                >
                    <span>&#43;</span> Invite
                </button>
            </div>
        );
    }

    function renderNoInvitedMemberBanner() {
        if (orgMembers.length > 1) return null;
        return (
            <div className="announcement-container">
                <div className="announcement-blue-pill centered">NOTE</div>
                <div>
                    There are no additional users currently part of your organization. Please click the <b>'Invite'</b> button to send invite
                    requests and add other members to your organization.
                </div>
            </div>
        );
    }

    function handleRemoveMember(member: CurrentMember) {
        setState({ status: "delete-member", member });
    }
    function handleRoleUpdate(member: CurrentMember) {
        setState({ status: "update-member-role", member });
    }

    function getCurrentMemberActions(currentMember: CurrentMember) {
        if (!org.organizationFound) return [];
        const allActions = [
            {
                title: "Remove Member",
                clickHandler: handleRemoveMember,
                allow: canDeleteMember,
                id: "1"
            },
            {
                title: "Update Role",
                clickHandler: handleRoleUpdate,
                allow: canUpdateMemberRole,
                id: "2"
            }
        ];

        return allActions.filter(action => action.allow(currentMember, { role: org.role, email: user.email }));
    }

    function getPendingMemberActions(pendingMember: PendingMember) {
        if (!org.organizationFound) return [];
        const allActions = [
            {
                title: "Copy Link",
                clickHandler: handleCopyInviteLink,
                allow: canCopyInviteLink,
                id: "1"
            },
            {
                title: "Resend Link",
                clickHandler: handleResendInviteLink,
                allow: canResendInvite,
                id: "2"
            },
            {
                title: "Delete Invite",
                clickHandler: handleRevokeInviteLink,
                allow: canRevokeInvite,
                id: "3"
            }
        ];

        return allActions.filter(action => action.allow(pendingMember, { role: org.role, email: user.email }));
    }

    function renderCurrentMembersMenu(currentMember: CurrentMember) {
        const allowedActions = getCurrentMemberActions(currentMember);
        if (allowedActions.length === 0) return null;

        return (
            <div id="member-list-menu">
                <Menubar>
                    <MenubarTrigger>
                        <div className="member-list-menu-trigger">
                            <svg width="9" height="6" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M0.640274 2.1578C0.294467 1.85836 0.275757 1.32827 0.599591 1.00519C0.88677 0.718686 1.34463 0.697266 1.6573 0.955709L4.5 3.30536L7.38088 0.951921C7.69357 0.696479 8.14986 0.723362 8.43039 1.01375C8.73983 1.33407 8.71692 1.8485 8.38023 2.14005L4.5 5.5L0.640274 2.1578Z"
                                    fill="#959595"
                                />
                            </svg>
                        </div>
                    </MenubarTrigger>
                    <MenuContent>
                        {allowedActions.map(({ title, clickHandler, id }) => (
                            <MenubarItem
                                type={"regular"}
                                onClick={() => {
                                    clickHandler(currentMember);
                                }}
                                key={id}
                            >
                                {title}
                            </MenubarItem>
                        ))}
                    </MenuContent>
                </Menubar>
            </div>
        );
    }

    function renderPendingMembersMenu(pendingMember: PendingMember) {
        const allowedActions = getPendingMemberActions(pendingMember);
        if (allowedActions.length === 0) return null;

        return (
            <div id="member-list-menu">
                <Menubar>
                    <MenubarTrigger>
                        <div className="member-list-menu-trigger">
                            <svg width="9" height="6" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M0.640274 2.1578C0.294467 1.85836 0.275757 1.32827 0.599591 1.00519C0.88677 0.718686 1.34463 0.697266 1.6573 0.955709L4.5 3.30536L7.38088 0.951921C7.69357 0.696479 8.14986 0.723362 8.43039 1.01375C8.73983 1.33407 8.71692 1.8485 8.38023 2.14005L4.5 5.5L0.640274 2.1578Z"
                                    fill="#959595"
                                />
                            </svg>
                        </div>
                    </MenubarTrigger>
                    <MenuContent>
                        {allowedActions.map(({ id, title, clickHandler }) => (
                            <MenubarItem
                                type={"regular"}
                                onClick={() => {
                                    clickHandler(pendingMember);
                                }}
                                key={id}
                            >
                                {title}
                            </MenubarItem>
                        ))}
                    </MenuContent>
                </Menubar>
            </div>
        );
    }
    function renderOrgMembers() {
        let currentMembers = orgMembers.filter(isCurrentMember);
        const pendingInvitations = orgMembers.filter(isPendingMember);

        return (
            <>
                {currentMembers.length > 0 && (
                    <div className="org-members" style={{ marginBottom: "24px" }}>
                        <h3>Current Members</h3>
                        <div className="member-list">
                            <div className="member-list-heading xl-only">
                                <span>Email</span>
                                <span>Name</span>
                                <span>Role</span>
                                <span />
                            </div>
                            <hr className="xl-only" />

                            {currentMembers.map(member => {
                                const { email, name, userId, role } = member;
                                const isActiveUser = email === user.email;

                                return (
                                    <div className="member-list-body" key={userId}>
                                        <div className="member-list-detail">
                                            <div className="member-email">
                                                {email}
                                                <em>{`${isActiveUser ? " (you)" : ""}`}</em>
                                            </div>
                                            <div className="member-name">
                                                <span>{name}</span>
                                            </div>
                                            <div className="member-role">
                                                <span>{capitalizeFirstLetter(role)}</span>
                                            </div>
                                        </div>
                                        <div className="member-options">{renderCurrentMembersMenu(member)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {pendingInvitations.length > 0 && (
                    <div className="org-members" style={{ marginBottom: "24px" }}>
                        <h3>Pending Invitations</h3>
                        <div className="member-list pending">
                            <div className="pending-member-list-heading xl-only">
                                <span>Email</span>
                                <span>Role</span>
                                <span />
                                <span />
                            </div>
                            <hr className="xl-only" />
                            {pendingInvitations.map(member => {
                                const { email, role } = member;
                                return (
                                    <div className="member-list-body" key={email}>
                                        <div className="member-list-detail">
                                            <div className="member-email">{email}</div>
                                            <div className="member-role">
                                                <span>{capitalizeFirstLetter(role)}</span>
                                            </div>
                                            <div className="member-name" />
                                        </div>
                                        <div className="member-options">{renderPendingMembersMenu(member)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </>
        );
    }

    function renderModals() {
        switch (state.status) {
            case "create-organization":
                return (
                    <CreateOrgModal
                        isModalOpen
                        closeModal={() => {
                            setState({ status: "idle" });
                        }}
                        onOrgCreated={org => {
                            onOrgCreated(org);
                            setState({ status: "invite-member" });
                        }}
                        user={user}
                    />
                );
            case "delete-member": {
                return (
                    <DeleteMemberModal
                        member={state.member}
                        isModalOpen
                        closeModal={() => {
                            setState({ status: "idle" });
                        }}
                        onMemberDeleted={onMemberDeleted}
                        org={org}
                        user={user}
                    />
                );
            }

            case "invite-member": {
                return (
                    <InviteMemberModal
                        isModalOpen
                        closeModal={() => {
                            setState({ status: "idle" });
                        }}
                        onMembersInvited={onMembersInvited}
                        orgMembers={orgMembers}
                        maxNumberOfMembers={maxNumberOfMembers}
                        org={org}
                        user={user}
                    />
                );
            }
            case "idle":
                return null;
            case "update-member-role": {
                if (state.member.role === "owner") {
                    // owner's role can not be updated
                    return null;
                }
                return (
                    <UpdateMemberRoleModal
                        isModalOpen
                        closeModal={() => {
                            setState({ status: "idle" });
                        }}
                        onMemberRoleUpdated={onMemberUpdated}
                        member={state.member}
                        org={org}
                        user={user}
                    />
                );
            }

            default:
                assertNever(state);
        }
    }

    return (
        <div className="centered-box padded-y" style={{ position: "relative" }}>
            {renderHeader()}
            <hr />
            <div className="padded-x">
                {renderNoInvitedMemberBanner()}
                {renderOrgMembers()}
            </div>
            {renderModals()}
            {isProcessing && <OverlaySpinner />}
        </div>
    );
}
